import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateTenantMutation, TENANT_KEYS } from "../../hooks/useTenants";
import {
  useAdminTenantMarketing,
  ADMIN_TENANT_MARKETING_KEY,
} from "../../hooks/useAdminTenantDeletion";
import { useToast } from "../../contexts/ToastContext";
import type { Tenant, UpdateTenantPayload } from "../../services/tenantApi";
import type { ClientSource } from "../../services/tenantDeletionApi";
import { CLIENT_SOURCE_LABELS } from "../../services/tenantDeletionApi";

interface MarketingSalesFormProps {
  tenant: Tenant;
}

const NOTE_MAX = 500;
/** Screen-reader "characters remaining" announcement fires once this few characters are left. */
const NOTE_WARNING_REMAINING = 50;

const formatConsentDate = (iso: string | null): string => {
  if (!iso) return "";
  return new Intl.DateTimeFormat("pl-PL").format(new Date(iso));
};

/**
 * Form for marketing & sales attributes:
 * - "Źródło klienta" (clientSource) — original behaviour.
 * - AC-4 read-only consents (newsletter per member, Meta Pixel cookie consent).
 * - AC-1/AC-2/AC-12 manual "NIE WYSYŁAĆ" opt-out flag + note.
 *
 * marketingOptOut/marketingOptOutNote are NOT part of the `tenant` prop — they
 * live in the AC-4 read model (GET /admin/tenants/:id/marketing), fetched here.
 * If that fetch fails, the flag form still works (starts from false/empty) —
 * the consents section shows its own error independently (spec edge case).
 */
export const MarketingSalesForm: React.FC<MarketingSalesFormProps> = ({
  tenant,
}) => {
  const queryClient = useQueryClient();
  const updateMutation = useUpdateTenantMutation();
  const { addToast } = useToast();
  const marketingQuery = useAdminTenantMarketing(tenant.id);

  // --- clientSource (existing) ---------------------------------------------

  const tenantSource: ClientSource | null = tenant.clientSource ?? null;
  const [selected, setSelected] = useState<ClientSource | "">(
    tenantSource ?? ""
  );

  React.useEffect(() => {
    setSelected(tenant.clientSource ?? "");
    // Intentionally narrow deps: only resync on identity or version change, not every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id, tenant.updatedAt, tenant.clientSource]);

  // --- marketing opt-out flag + note (AC-12) --------------------------------

  const marketingData = marketingQuery.data;
  const marketingSettled = marketingQuery.isSuccess || marketingQuery.isError;
  const initialOptOut = marketingData?.marketingOptOut ?? false;
  const initialNote = marketingData?.marketingOptOutNote ?? null;

  const [optOut, setOptOut] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (marketingQuery.isSuccess) {
      setOptOut(marketingData?.marketingOptOut ?? false);
      setNote(marketingData?.marketingOptOutNote ?? "");
    } else if (marketingQuery.isError) {
      setOptOut(false);
      setNote("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id, marketingQuery.isSuccess, marketingQuery.isError, marketingData]);

  const clientSourceDirty =
    selected !== "" && selected !== (tenantSource ?? "");
  const flagChanged = marketingSettled && optOut !== initialOptOut;
  const normalizedNote = note === "" ? null : note;
  const noteChanged = marketingSettled && normalizedNote !== initialNote;

  const isDirty = clientSourceDirty || flagChanged || noteChanged;
  const canSubmit = isDirty && !updateMutation.isPending;

  const brandName = tenant.brand?.displayName ?? tenant.name;
  const membersCount = marketingData?.members.length ?? 0;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;

    if (flagChanged) {
      let message: string;
      if (!optOut) {
        message = "Odznaczenie nie przywróci wypisanych osób do Kit.com.";
      } else if (marketingQuery.isError) {
        // Real member count is unknown (GET /admin/tenants/:id/marketing failed) —
        // never claim "0 osób", or the confirm falsely reassures before an
        // irreversible unsubscribe of ALL members (spec's only mitigant here).
        message = `Wypiszesz wszystkich członków organizacji (liczby nie udało się pobrać) z Kit.com marki ${brandName}. Tego nie da się cofnąć — ponowny zapis wymaga zgody tych osób.`;
      } else {
        message = `Wypiszesz ${membersCount} osób z Kit.com marki ${brandName}. Tego nie da się cofnąć — ponowny zapis wymaga zgody tych osób.`;
      }
      if (!window.confirm(message)) return;
    }

    const tenantData: UpdateTenantPayload = {};
    if (clientSourceDirty) tenantData.clientSource = selected as ClientSource;
    if (flagChanged) tenantData.marketingOptOut = optOut;
    if (noteChanged) tenantData.marketingOptOutNote = normalizedNote;

    updateMutation.mutate(
      {
        tenantId: tenant.id,
        tenantData,
        silent: flagChanged,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: TENANT_KEYS.detail(tenant.id),
          });
          if (flagChanged) {
            queryClient.invalidateQueries({
              queryKey: [...ADMIN_TENANT_MARKETING_KEY, tenant.id],
            });
            addToast({
              message: "Zapisano. Wypisywanie z Kit.com w toku.",
              type: "success",
            });
          }
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      {initialOptOut && (
        <div
          role="status"
          data-testid="marketing-opt-out-banner"
          className="rounded-md border border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700"
        >
          NIE WYSYŁAĆ — maili marketingowych i ofert
        </div>
      )}

      <section className="space-y-2">
        <h3 className="text-sm font-medium text-neutral-700">Zgody</h3>

        {marketingQuery.isLoading && (
          <p
            data-testid="marketing-consents-loading"
            className="text-sm text-neutral-500"
          >
            Ładowanie zgód…
          </p>
        )}

        {marketingQuery.isError && (
          <p
            data-testid="marketing-consents-error"
            className="text-sm text-red-600"
          >
            Nie udało się pobrać zgód marketingowych.
          </p>
        )}

        {marketingQuery.isSuccess && marketingData && (
          <>
            {marketingData.members.length === 0 ? (
              <p
                data-testid="marketing-members-empty"
                className="text-sm text-neutral-500"
              >
                Organizacja nie ma żadnych członków.
              </p>
            ) : (
              <ul
                data-testid="marketing-members-list"
                className="divide-y divide-neutral-200 border border-neutral-200 rounded-md"
              >
                {marketingData.members.map((m) => (
                  <li
                    key={m.userId}
                    data-testid={`marketing-member-${m.userId}`}
                    className="px-3 py-2 text-sm flex justify-between items-center gap-2"
                  >
                    <span>
                      {m.firstName} {m.lastName} ({m.email})
                    </span>
                    <span className="text-right">
                      {m.newsletterConsent ? "Tak" : "Nie"}
                      {m.newsletterConsentAt &&
                        ` — ${formatConsentDate(m.newsletterConsentAt)}`}
                      {m.suppressedByOtherOrg && (
                        <span className="ml-2 text-xs text-amber-700">
                          (wstrzymany przez inną organizację)
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div
              data-testid="marketing-meta-consent-row"
              className="text-sm flex justify-between items-center border-t border-neutral-200 pt-2"
            >
              <span>Cookies marketingowe (Meta Pixel)</span>
              <span>
                {marketingData.marketingConsent === null
                  ? "Brak danych"
                  : marketingData.marketingConsent
                    ? "Tak"
                    : "Nie"}
              </span>
            </div>
            <p className="text-xs text-neutral-500">
              To NIE jest zgoda na e-mail — dotyczy wyłącznie plików cookies
              (Meta Pixel).
            </p>
          </>
        )}
      </section>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor={`client-source-${tenant.id}`}
            className="block text-sm font-medium text-neutral-700 mb-1"
          >
            Źródło klienta
          </label>
          <select
            id={`client-source-${tenant.id}`}
            value={selected}
            onChange={(e) =>
              setSelected(e.target.value as ClientSource | "")
            }
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">— Nieustawione —</option>
            <option value="inbound">{CLIENT_SOURCE_LABELS.inbound}</option>
            <option value="outbound">{CLIENT_SOURCE_LABELS.outbound}</option>
          </select>
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-neutral-700">
            <input
              type="checkbox"
              data-testid="marketing-opt-out-checkbox"
              checked={optOut}
              disabled={!marketingSettled}
              onChange={(e) => setOptOut(e.target.checked)}
            />
            NIE WYSYŁAĆ — maili marketingowych i ofert
          </label>
          {marketingQuery.isError && (
            <p
              data-testid="marketing-opt-out-unknown-state"
              className="mt-1 text-xs text-red-600"
            >
              Bieżący stan tej flagi jest nieznany (patrz błąd wyżej) —
              zaznaczenie dotyczy wszystkich członków organizacji.
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="marketing-opt-out-note"
            className="block text-sm font-medium text-neutral-700 mb-1"
          >
            Notatka
          </label>
          <textarea
            id="marketing-opt-out-note"
            data-testid="marketing-opt-out-note"
            value={note}
            disabled={!marketingSettled}
            maxLength={NOTE_MAX}
            rows={3}
            onChange={(e) => setNote(e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-neutral-100 disabled:cursor-not-allowed"
          />
          <div
            data-testid="marketing-opt-out-note-counter"
            aria-live="off"
            className="mt-1 text-xs text-neutral-500"
          >
            {note.length}/{NOTE_MAX}
          </div>
          <div
            data-testid="marketing-opt-out-note-live-region"
            aria-live="polite"
            className="sr-only"
          >
            {NOTE_MAX - note.length <= NOTE_WARNING_REMAINING
              ? `Pozostało ${NOTE_MAX - note.length} znaków z ${NOTE_MAX}.`
              : ""}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!canSubmit}
            className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-4 py-2 rounded-md font-medium transition-colors text-sm"
          >
            {updateMutation.isPending ? "Zapisywanie..." : "Zapisz"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MarketingSalesForm;
